/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { call, delay, put, race } from "redux-saga/effects";

import * as DistributionListApi from "Services/DistributionListApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { IDistributionListFetchRequest } from "Interfaces/DistributionLists/IDistributionListFetchRequest";
import { IDistributionListReviewRequest } from "Interfaces/DistributionLists/IDistributionListReviewRequest";
import { IDistributionListsByTenant } from "Interfaces/DistributionLists/IDistributionListsByTenantResult";
import { IDistributionListsDataResult } from "Interfaces/DistributionLists/IDistributionListsDataResult";
import { IDistributionListsRequest } from "Interfaces/DistributionLists/IDistributionListsRequest";
import { IDistributionListsResult } from "Interfaces/DistributionLists/IDistributionListsResult";
import { IDLMembersResult } from "Interfaces/DistributionLists/IDLMembersResult";
import { IApiEmptyResult, IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  addMicrosoftMemberToDLFail,
  addMicrosoftMemberToDLSuccess,
  approveOrDeclineJoinRequestFail,
  approveOrDeclineJoinRequestSuccess,
  fetchDistributionListsByTenantFail,
  fetchDistributionListsByTenantSuccess,
  fetchMicrosoftMembersForDLFail,
  fetchMicrosoftMembersForDLSuccess,
  removeMicrosoftMemberFromDLFail,
  removeMicrosoftMemberFromDLSuccess,
  searchDistributionListsFail,
  searchDistributionListsSuccess,
  IAddMicrosoftMemberToDLRequest,
  IApproveOrDeclineJoinRequest,
  IFetchDistributionListByTenantRequest,
  IFetchMicrosoftMembersForDLRequest,
  IRemoveMicrosoftMemberFromDLRequest,
  ISearchDistributionLists,
} from "./DistributionListsAction";

/**
 * Search all distribution lists
 * @param {ISearchDistributionLists} action for searching benefits
 * @returns {void}
 */
function* handleSearchDistributionLists(action: ISearchDistributionLists) {
  const failedMessage = "Failed to search the distribution lists.";
  const requestBody = {
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
  } as unknown as IDistributionListsRequest;

  try {
    const { result, timeout }: IApiResult2<IDistributionListsDataResult> | any = yield race({
      result: call(DistributionListApi.searchDistributionLists, requestBody, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchDistributionListsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IDistributionListsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchDistributionListsSuccess(
            parsedBody.requests,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          searchDistributionListsFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(searchDistributionListsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchDistributionListsFail(error));
  }
}

/**
 * Approve or decline join request
 * @param {IApproveOrDeclineJoinRequest} action for approving or declining a join request
 * @returns {void}
 */
function* handleApproveOrDeclineJoinRequest(action: IApproveOrDeclineJoinRequest) {
  const failedMessage = "Failed to approve or decline the distribution list join request.";

  try {
    const request = {
      tenant: action.tenant,
      requestId: action.requestId,
      reviewAction: action.reviewAction,
    } as IDistributionListReviewRequest;

    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(DistributionListApi.approveOrDeclineDlJoinRequest, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(approveOrDeclineJoinRequestFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(approveOrDeclineJoinRequestSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          approveOrDeclineJoinRequestFail((result.parsedBody as IApiErrorResult)?.error?.message)
        );
        break;
      default:
        yield put(approveOrDeclineJoinRequestFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(approveOrDeclineJoinRequestFail(error));
  }
}

/**
 * Fetch a list of DLs for a tenant.
 * @param {IFetchDistributionListByTenantRequest} action for fetching distribution lists by tenant
 * @returns {void}
 */
function* handleFetchDistributionListByTenantRequest(
  action: IFetchDistributionListByTenantRequest
) {
  const failedMessage = "Failed to fetch list of DLs for tenant.";

  try {
    const request = {
      tenant: action.tenant,
      pageSize: action.pageSize,
      pageNumber: action.pageNumber,
    } as IDistributionListFetchRequest;

    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(DistributionListApi.fetchDistributionListsByTenant, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchDistributionListsByTenantFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IDistributionListsByTenant;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          fetchDistributionListsByTenantSuccess(parsedBody.distributionGroups, parsedBody.count)
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchDistributionListsByTenantFail((result.parsedBody as IApiErrorResult)?.error?.message)
        );
        break;
      default:
        yield put(fetchDistributionListsByTenantFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchDistributionListsByTenantFail(error));
  }
}

/**
 * Fetch a list of microsoft members for a given distribution list.
 * @param {IFetchMicrosoftMembersForDLRequest} action for fetching list of microsoft members for a DL
 * @returns {void}
 */
function* handleFetchMicrosoftMembersForDL(action: IFetchMicrosoftMembersForDLRequest) {
  const failedMessage = "Failed to fetch list of DLs for tenant.";

  try {
    const request = {
      tenant: action.tenant,
      dlAlias: action.dlAlias,
    } as IFetchMicrosoftMembersForDLRequest;

    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(DistributionListApi.fetchMicrosoftMembersForDL, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchMicrosoftMembersForDLFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IDLMembersResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(fetchMicrosoftMembersForDLSuccess(parsedBody.distributionGroupMembers));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchMicrosoftMembersForDLFail((result.parsedBody as IApiErrorResult)?.error?.message)
        );
        break;
      default:
        yield put(fetchMicrosoftMembersForDLFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchMicrosoftMembersForDLFail(error));
  }
}

/**
 * Add a microsoft member to a DL.
 * @param {IAddMicrosoftMemberToDLRequest} action for adding a microsoft member to a DL.
 * @returns {void}
 */
function* handleAddMicrosoftMemberToDL(action: IAddMicrosoftMemberToDLRequest) {
  const failedMessage = "Failed to add microsoft member to DL.";

  try {
    const request = {
      tenant: action.tenant,
      dlAlias: action.dlAlias,
      displayName: action.displayName,
      firstName: action.firstName,
      lastName: action.lastName,
      email: action.email,
    } as IAddMicrosoftMemberToDLRequest;

    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(DistributionListApi.addMicrosoftMemberToDL, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addMicrosoftMemberToDLFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(addMicrosoftMemberToDLSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          addMicrosoftMemberToDLFail((result.parsedBody as IApiErrorResult)?.error?.message)
        );
        break;
      default:
        yield put(addMicrosoftMemberToDLFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(addMicrosoftMemberToDLFail(error));
  }
}

/**
 * Remove a microsoft member from a distribution list
 * @param {IRemoveMicrosoftMemberFromDLRequest} action for removing a Microsoft member from a DL.
 * @returns {void}
 */
function* handleRemoveMicrosoftMemberFromDL(action: IRemoveMicrosoftMemberFromDLRequest) {
  const failedMessage = "Failed to remove microsoft member from DL.";

  try {
    const request = {
      tenant: action.tenant,
      dlAlias: action.dlAlias,
      memberEmail: action.memberEmail,
    } as IRemoveMicrosoftMemberFromDLRequest;

    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(DistributionListApi.removeMicrosoftMemberFromDL, request),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(removeMicrosoftMemberFromDLFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.NoContent:
        yield put(removeMicrosoftMemberFromDLSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          removeMicrosoftMemberFromDLFail((result.parsedBody as IApiErrorResult)?.error?.message)
        );
        break;
      default:
        yield put(removeMicrosoftMemberFromDLFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(removeMicrosoftMemberFromDLFail(error));
  }
}

export {
  handleSearchDistributionLists,
  handleApproveOrDeclineJoinRequest,
  handleFetchDistributionListByTenantRequest,
  handleFetchMicrosoftMembersForDL,
  handleAddMicrosoftMemberToDL,
  handleRemoveMicrosoftMemberFromDL,
};
